import React, { useRef, useState, useEffect } from 'react';
import ThemeToggleButton from './ThemeToggleButton';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, logout } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, ChevronDown } from 'lucide-react';

const TopBar = () => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef();

  const handleSettings = () => {
    setShowDropdown(false);
    navigate('/settings');
  };

  const handleLogout = () => {
    setShowDropdown(false);
    dispatch(logout());
    navigate('/login');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <header
      className="w-full flex items-center justify-between px-6 py-3 shadow z-40"
      style={{
        background: 'var(--sidebar-bg)',
        borderBottom: '1px solid var(--sidebar-border)',
        color: 'var(--sidebar-text)'
      }}
    >
      <div className="flex-1" />

      <div className="flex items-center space-x-4">
        <ThemeToggleButton />
        {user && (
          <div className="relative" ref={dropdownRef}>
            <button
              className="flex items-center space-x-2 focus:outline-none"
              onClick={() => setShowDropdown((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={showDropdown}
              aria-label="Account menu"
            >
              <div style={{ background: 'var(--brand)' }} className="rounded-full p-1">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <span className="font-medium hidden sm:block" style={{ color: 'var(--sidebar-text)' }}>
                {user.userName}
              </span>
              {/* Without this the name reads as a label, not something to
                  click — the menu behind it went unnoticed. */}
              <ChevronDown
                size={16}
                className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`}
                style={{ color: 'var(--sidebar-text)' }}
              />
            </button>
            {showDropdown && (
              <div
                className="
                  absolute right-0 mt-2 w-56 rounded-lg shadow-dropdown py-2
                  transition-all z-50
                "
                style={{
                  background: 'var(--card-bg)',
                  color: 'var(--main-text)',
                  border: '1px solid var(--card-border)'
                }}
              >
                <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--card-border)' }}>
                  <div className="font-bold" style={{ color: 'var(--main-text)' }}>{user.userName}</div>
                  <div className="text-xs" style={{ color: 'var(--main-text-muted)' }}>{user.email}</div>
                </div>
                <button
                  onClick={handleSettings}
                  className="w-full flex items-center gap-2 text-left px-4 py-2 font-medium"
                  style={{
                    color: 'var(--main-text)',
                    background: 'transparent'
                  }}
                  onMouseDown={e => e.preventDefault()}
                >
                  <Settings size={16} />
                  Settings
                </button>

                <div className="my-1 border-t" style={{ borderColor: 'var(--card-border)' }} />

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 text-left px-4 py-2 font-medium"
                  style={{
                    color: 'var(--danger)',
                    background: 'transparent'
                  }}
                  onMouseDown={e => e.preventDefault()}
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default TopBar;